import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { CloudActions } from '@portal/portal-core';
import { SuperAdminActions } from 'actions';
import { clearUsers } from 'ducks/super-client/actions';
import { useAppDispatch } from 'store/hooks';
import FjSortTable from 'components/table/fj-sort-table';
import { CheckboxCell, NameCell } from 'components/table/table-cells';
import TableCheckbox from 'components/table/table-checkbox';
import { filterSelector } from 'selectors';
import {
  selectActiveUserIds,
  selectCombinedFilter,
  selectFilterIsActive,
  selectLicenseRequestProgress,
  selectSearchStatus,
  // selectUnPhantomUsersResults,
  selectUserCCs,
  selectUsers,
  selectVerifyEmailsResults,
} from 'selectors/super-admin';
import LicenseDetailModal from './license-detail-modal';
import UserDetailModal from './user-detail-modal';

// Minimal shape of the fields read off a super-admin user row. The rows originate
// from untyped `.js` state slices, so only the accessed fields are modeled here.
interface User {
  id: string;
  primaryEmail?: string;
  firstname?: string;
  lastname?: string;
  verified?: boolean;
  isVerified?: boolean;
  isPhantom?: boolean;
  accountState?: string;
  countryCode?: string;
}

// The child modals each model the shared license/user domain object with their own
// local (non-exported) interfaces. Recover those exact prop types so this component
// stays in lockstep with them without duplicating the shapes.
type LicenseModalLicense = React.ComponentProps<typeof LicenseDetailModal>['license'];
type UserModalUser = React.ComponentProps<typeof UserDetailModal>['user'];
type UserModalUserCCs = React.ComponentProps<typeof UserDetailModal>['userCCs'];
type UserModalSetLicense = React.ComponentProps<typeof UserDetailModal>['setSelectedLicense'];

interface SearchStatus {
  requestId: string | null;
  error: string | null;
}

interface VerifyEmailResult {
  succeeded: boolean;
  data: User;
}

interface TableState {
  page: number;
  pageSize: number;
  sortBy: string | null;
  desc: boolean | null;
}

// Shape of the state object FjSortTable passes to `onFetchData`.
interface FetchDataState {
  page: number;
  pageSize: number;
  sorted?: Array<{ id?: string; desc?: boolean }>;
}

interface FilterState {
  filterByStatus?: string;
  filterByValue?: string;
}

interface UsersTableProps {
  filterControls: React.ReactNode;
  controls: React.ReactNode;
  selectedRowsByID?: unknown;
  setCount: (count: number) => void;
  [key: string]: unknown;
}

const UsersTable = ({
  filterControls,
  controls,
  selectedRowsByID,
  setCount,
  ...props
}: UsersTableProps): React.JSX.Element => {
  // `selectActiveUserIds` reads an untyped `.js` selection map (userId -> selected flag).
  const selectedRows = useSelector(selectActiveUserIds) as Record<string, boolean>;
  const requestProgress = useSelector(selectLicenseRequestProgress);
  // `filterSelector` reads from untyped `.js` state slices; narrow to the fields used here.
  const filter = useSelector(filterSelector) as FilterState;
  const superAdminFilter = useSelector(selectCombinedFilter) as (user: User) => boolean;
  const filterIsActive = useSelector(selectFilterIsActive);
  const verifyEmailsResults = useSelector(selectVerifyEmailsResults) as VerifyEmailResult[];
  // const unPhantomUsersResults = useSelector(selectUnPhantomUsersResults);
  const userCCs = useSelector(selectUserCCs);
  const users = useSelector(selectUsers) as User[];
  const searchStatus = useSelector(selectSearchStatus) as SearchStatus;

  const dispatch = useAppDispatch();
  const selectRow = (user: User) => dispatch(SuperAdminActions.selectUserRow(user));
  const deselectRow = (user: User) => dispatch(SuperAdminActions.deselectUserRow(user));
  const deselectAll = () => dispatch(SuperAdminActions.deselectAllUserRows());
  const getUserCCs = (userId: string) => dispatch(CloudActions.getCostCentersByUser(userId));
  const fetchUsers = () => dispatch(SuperAdminActions.fetchSearchUsers());

  const [data, setData] = useState<User[]>([]);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [tableState, setTableState] = useState<TableState>({
    page: 1,
    pageSize: 20,
    sortBy: null,
    desc: null,
  });
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedLicense, setSelectedLicense] = useState<LicenseModalLicense | null>(null);

  React.useEffect(() => {
    if (!filter.filterByValue || filter.filterByValue.length < 3) {
      dispatch(clearUsers());
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchUsers();
  }, [filter.filterByValue]);

  // Clear the loading spinner once the saga signals search completion (success or error).
  // Using requestId rather than users array so this fires even when results are empty and the
  // deep-equality memoized selector returns the same [] reference.
  React.useEffect(() => {
    if (searchStatus.requestId !== null || searchStatus.error !== null) {
      setLoading(false);
    }
  }, [searchStatus]);

  React.useEffect(() => {
    // DOES THIS EVEN WORK CURRENTLY?
    const updatedUsers = verifyEmailsResults.reduce<User[]>(
      (acc, result) => (result.succeeded ? [...acc, result.data] : acc),
      [],
    );
    if (updatedUsers.length) {
      const newData = data.map((user) => {
        const updatedUser = updatedUsers.find(({ id }) => id === user.id);
        return updatedUser || user;
      });
      setData(newData);
    }
    //
    // const updatedUsersPhantom = unPhantomUsersResults.reduce(
    //   (acc, result) => (result.succeeded ? [...acc, result.data] : acc),
    //   []
    // );
    // if (updatedUsersPhantom.length) {
    //   const newData = data.map((user) => {
    //     const updatedUser = updatedUsersPhantom.find(({ id }) => id === user.id);
    //     return updatedUser || user;
    //   });
    //   setData(newData);
    // }
  }, [verifyEmailsResults]);

  const isShowingPlaceholder = !loading && data.length === 0;

  const columns = [
    {
      id: 'select',
      path: 'id',
      Cell: ({ value, original }: { value: string; original: User }) => (
        <TableCheckbox
          rowId={value}
          selectRow={() => selectRow(original)}
          deselectRow={() => deselectRow(original)}
          selectedRows={selectedRows}
          disabled={original.verified}
        />
      ),
      CellContainer: CheckboxCell,
      width: 40,
      resizable: false,
      sortable: false,
    },
    {
      id: 'fullname',
      path: ({ primaryEmail, firstname, lastname }: User) => ({
        email: primaryEmail,
        firstname,
        lastname,
      }),
      Header: () => 'Name',
      Cell: (
        { value, original }: { value: { email?: string; firstname?: string; lastname?: string }; original: User },
        _props: Record<string, unknown>,
      ) => (
        <NameCell
          {...value}
          {..._props}
          onClick={() => {
            setSelectedUser(original);
            getUserCCs(original.id);
          }}
        />
      ),
      minWidth: 250,
      alignLeft: true,
    },
    {
      id: 'userId',
      path: 'id',
      Header: () => 'User ID',
      width: 240,
      resizable: false,
    },
    {
      path: 'isVerified',
      Header: () => 'Email Verified',
      minWidth: 140,
    },
    {
      path: 'isPhantom',
      Header: () => 'Phantom User',
      minWidth: 140,
    },
    {
      path: 'accountState',
      Header: () => 'Account Status',
      minWidth: 140,
    },
    {
      path: 'countryCode',
      Header: () => 'Country',
      minWidth: 110,
    },
  ];

  const updateTableState = (state: FetchDataState) => {
    const { page, pageSize, sorted } = state;
    const { id: sortBy = 'fullname', desc = true } = sorted?.[0] || {};
    setTableState({ page, pageSize, sortBy, desc });
  };

  const sortUsers = React.useCallback(
    (usersDb: User[]): User[] => {
      const { sortBy, desc } = tableState;
      const rows = usersDb as unknown as Array<Record<string, string | undefined>>;
      rows.sort(
        (a, b) =>
          // Preserve the original loose coercions: `test(undefined)` -> true and NaN
          // arithmetic when a sort key is absent. Casts are compile-only.
          Number(/[A-Za-z]/.test(a[sortBy!]?.toUpperCase() as string)) -
          Number(/[A-Za-z]/.test(b[sortBy!]?.toUpperCase() as string)) ||
          (a[sortBy!]?.charCodeAt(0) as number) - (b[sortBy!]?.charCodeAt(0) as number) ||
          0, // treat nulls as blank
      );
      return desc ? usersDb.reverse() : usersDb;
    },
    [tableState.sortBy, tableState.desc],
  );

  const filterUsers = React.useCallback(
    (usersDb: User[]): User[] => {
      if (filterIsActive) {
        return usersDb.filter((user) => (filterIsActive ? superAdminFilter(user) : true));
      }
      return usersDb;
    },
    [superAdminFilter],
  );

  const paginateUsers = React.useCallback(
    (usersDb: User[]): User[] => {
      const { page, pageSize } = tableState;
      const firstRecord = page * pageSize;
      return usersDb.slice(firstRecord, firstRecord + pageSize);
    },
    [tableState.page, tableState.pageSize],
  );

  React.useEffect(() => {
    deselectAll();

    const { pageSize } = tableState;

    const filteredUsersDb = filterUsers(users);
    const sortedUsersDb = sortUsers(filteredUsersDb);
    const count = sortedUsersDb.length;
    const usersPageDb = paginateUsers(sortedUsersDb);

    setData(usersPageDb);
    setCount(count);
    setPages(Math.ceil(count / pageSize));
  }, [tableState, superAdminFilter, users]);

  const getPlaceholderMessage = () => {
    if (!filter.filterByValue)
      return 'Click the magnifying glass and begin typing to search for users.';
    if (filter.filterByValue.length < 3) return 'Query string must be at least 3 characters.';
    return 'No users found matching your search.';
  };
  const placeholderMessage = getPlaceholderMessage();

  return (
    <>
      {!!selectedLicense && (
        <LicenseDetailModal
          license={selectedLicense}
          userId={selectedUser?.id}
          onRequestClose={() => setSelectedLicense(null)}
        />
      )}
      {!!selectedUser && (
        <UserDetailModal
          // The table row and the modals model the same untyped user/license domain
          // objects with divergent local interfaces; cast across that boundary.
          user={selectedUser as unknown as UserModalUser}
          onRequestClose={() => {
            setSelectedUser(null);
            setSelectedLicense(null);
          }}
          userCCs={userCCs as UserModalUserCCs}
          setSelectedLicense={setSelectedLicense as unknown as UserModalSetLicense}
        />
      )}
      <TableWrapper>
        <PaddedTable
          manual
          pages={isShowingPlaceholder ? 1 : pages}
          loading={loading}
          onFetchData={updateTableState}
          columns={columns}
          data={data}
          selectedRowsByID={selectedRows}
          filterControls={filterControls}
          controls={controls}
          sortable={!isShowingPlaceholder}
          disableControls={isShowingPlaceholder}
          percentLoaded={requestProgress}
          tableName="sa-users"
          {...props}
        />
        {isShowingPlaceholder && (
          <PlaceholderOverlay>
            <PlaceholderBox>{placeholderMessage}</PlaceholderBox>
          </PlaceholderOverlay>
        )}
      </TableWrapper>
    </>
  );
};

const PaddedTable = styled(FjSortTable)`
  padding-left: ${({ theme }) => theme.grid.units(1)};
`;

const TableWrapper = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  /* This is the flex item the super-admin page hands to FjFlexContainer's row.
     A flex item defaults to min-width: auto and refuses to shrink below its
     content, so the wrapper stays as wide as the columns need and the table has
     nothing left to scroll: the right-hand columns sit off the side of the
     window, unreachable, while the page itself overflows instead. At a 1280px
     viewport that left the last column 50px past the edge with a scroll range
     of zero. */
  min-width: 0;
`;

const PlaceholderOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`;

const PlaceholderBox = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.selectableLightBg};
  border-radius: 4px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.18);
  color: ${({ theme }) => theme.colors.mutedText};
  font-size: 1.25em;
  font-style: italic;
  font-weight: bold;
  padding: ${({ theme }) => theme.grid.units(1.5)} ${({ theme }) => theme.grid.units(3)};
`;

export default UsersTable;
